import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Users, Briefcase, AlertOctagon, AlertTriangle, Search, RefreshCw } from 'lucide-react';
import logger from '@/lib/logger';
import OccupationCellDrawer from './OccupationCellDrawer';

// Color band based on task count (lower is calm, higher is hot)
function cellTone(count) {
  if (!count) return { bg: 'bg-gray-50', text: 'text-gray-400', label: '—' };
  if (count <= 3) return { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'leve' };
  if (count <= 7) return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'moderada' };
  return { bg: 'bg-red-200', text: 'text-red-900', label: 'sobrecarga' };
}

function OccupationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerCell, setDrawerCell] = useState(null);    // { consultant, projectId }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/occupation');
      setData(res.data);
    } catch (e) {
      logger.error('Error fetching occupation:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build a lookup of consultant→project_id→tasks
  const lookup = useMemo(() => {
    if (!data) return {};
    const m = {};
    data.consultants.forEach((c) => {
      m[c.name] = {};
      c.projects.forEach((p) => {
        m[c.name][p.project_id] = p;
      });
    });
    return m;
  }, [data]);

  const filteredConsultants = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.consultants;
    return data.consultants.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.projects.some(
          (p) => (p.name || '').toLowerCase().includes(q) || (p.project_number || '').toLowerCase().includes(q)
        )
    );
  }, [data, search]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const { summary, projects } = data;

  return (
    <div className="space-y-5" data-testid="occupation-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <Users className="w-7 h-7 text-emerald-700" />
            <div>
              <p className="text-xs text-emerald-700">Consultores ativos</p>
              <p className="text-2xl font-bold text-emerald-800" data-testid="occupation-consultants-count">
                {summary.total_consultants}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-blue-200 bg-blue-50">
          <div className="flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-blue-700" />
            <div>
              <p className="text-xs text-blue-700">Projetos com alocação</p>
              <p className="text-2xl font-bold text-blue-800" data-testid="occupation-projects-count">
                {summary.total_active_projects_assigned}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-gray-700" />
            <div>
              <p className="text-xs text-gray-600">Tarefas ativas</p>
              <p className="text-2xl font-bold text-gray-800">{summary.total_active_tasks}</p>
              <p className="text-[10px] text-gray-500">
                {summary.total_overdue_tasks} atrasadas
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertOctagon className="w-7 h-7 text-red-700" />
            <div>
              <p className="text-xs text-red-700">Consultores sobrecarregados</p>
              <p className="text-2xl font-bold text-red-800" data-testid="occupation-overloaded">
                {summary.overloaded_consultants}
              </p>
              <p className="text-[10px] text-red-600">≥ 8 tarefas ativas</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 border border-gray-200">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar consultor ou projeto…"
              className="border-0 focus-visible:ring-0 shadow-none"
              data-testid="occupation-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="occupation-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Recalcular
          </Button>
        </div>
        <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200"></span>1-3 tarefas (leve)</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200"></span>4-7 tarefas (moderada)</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 border border-red-300"></span>8+ tarefas (sobrecarga)</span>
        </div>
      </Card>

      {/* Matrix */}
      {projects.length === 0 ? (
        <Card className="p-12 border border-gray-200 text-center">
          <p className="text-gray-500">Nenhum consultor alocado em projetos ativos no momento.</p>
        </Card>
      ) : (
        <Card className="p-0 border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="occupation-matrix">
              <thead>
                <tr>
                  <th className="py-3 px-3 bg-gray-50 border-b border-r border-gray-200 text-left text-gray-900 font-semibold sticky left-0 z-10 min-w-[200px]">
                    Consultor
                  </th>
                  {projects.map((p) => (
                    <th
                      key={p.project_id}
                      className="py-3 px-2 bg-gray-50 border-b border-gray-200 text-center text-gray-700 font-medium align-bottom"
                      title={p.name}
                      style={{ minWidth: 70 }}
                    >
                      <div className="flex flex-col items-center gap-1 max-w-[120px] mx-auto">
                        {p.project_number && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {p.project_number}
                          </Badge>
                        )}
                        <span className="text-[10px] line-clamp-2 break-words">
                          {p.name || '—'}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th className="py-3 px-3 bg-emerald-50 border-b border-l border-gray-200 text-center text-emerald-800 font-semibold sticky right-0">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredConsultants.map((c) => (
                  <tr key={c.name} className="border-b border-gray-100" data-testid={`occupation-row-${c.name}`}>
                    <td className="py-2 px-3 bg-white border-r border-gray-200 sticky left-0 z-10">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-bold shrink-0">
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{c.name}</p>
                          <p className="text-[10px] text-gray-500">{c.project_count} projeto(s)</p>
                        </div>
                      </div>
                    </td>
                    {projects.map((p) => {
                      const cell = lookup[c.name]?.[p.project_id];
                      const tone = cellTone(cell?.active_tasks);
                      return (
                        <td
                          key={p.project_id}
                          className={`py-2 px-1 text-center border-l border-gray-100 ${tone.bg} ${cell ? 'cursor-pointer hover:ring-2 hover:ring-emerald-400 hover:ring-inset' : ''} transition`}
                          title={cell ? `${cell.active_tasks} ativa(s) · ${cell.overdue_tasks} atrasada(s) · Clique para ver detalhes` : 'Sem alocação'}
                          onClick={cell ? () => setDrawerCell({ consultant: c.name, projectId: p.project_id }) : undefined}
                          data-testid={cell ? `occupation-cell-${c.name}-${p.project_id}` : undefined}
                        >
                          {cell ? (
                            <div className="flex flex-col items-center">
                              <span className={`font-bold ${tone.text}`}>{cell.active_tasks}</span>
                              {cell.overdue_tasks > 0 && (
                                <span className="text-[9px] text-red-700">⚠ {cell.overdue_tasks}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-2 px-3 bg-emerald-50 text-center border-l border-gray-200 sticky right-0">
                      <span className={`font-bold ${c.total_active_tasks >= 8 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {c.total_active_tasks}
                      </span>
                      {c.total_overdue_tasks > 0 && (
                        <div className="text-[9px] text-red-700">⚠ {c.total_overdue_tasks}</div>
                      )}
                    </td>
                  </tr>
                ))}

                {/* Column totals row */}
                <tr className="bg-emerald-50">
                  <td className="py-2 px-3 font-semibold text-emerald-800 border-r border-gray-200 sticky left-0">
                    Total por projeto
                  </td>
                  {projects.map((p) => (
                    <td key={p.project_id} className="py-2 px-1 text-center font-bold text-emerald-800">
                      {p.active_tasks}
                      <div className="text-[9px] text-emerald-600">{p.consultants} consultor(es)</div>
                    </td>
                  ))}
                  <td className="py-2 px-3 bg-emerald-100 text-center font-bold text-emerald-900 border-l border-gray-200 sticky right-0">
                    {summary.total_active_tasks}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {filteredConsultants.length === 0 && search && (
        <p className="text-center text-gray-500 text-sm">Nenhum consultor/projeto corresponde à busca.</p>
      )}

      <OccupationCellDrawer
        open={!!drawerCell}
        onOpenChange={(o) => { if (!o) setDrawerCell(null); }}
        consultant={drawerCell?.consultant}
        projectId={drawerCell?.projectId}
        onChange={fetchData}
      />
    </div>
  );
}

export default OccupationTab;
